import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  FileText,
  HelpCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';

const RECOMENDACION_TEXT = {
  CERRAR_ALERTA: 'Cerrar sin generar ROS',
  INVESTIGAR_MAS: 'Investigar más antes de decidir',
  EVALUAR_ROS: 'Considerar seriamente generar ROS',
  GENERAR_ROS: 'Altamente recomendado generar ROS'
};

export default function ROSEvaluationPanel({ casoId, onEvaluationComplete }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [scoreResult, setScoreResult] = useState(null);
  const [factoresSeleccionados, setFactoresSeleccionados] = useState({});
  const [checklist, setChecklist] = useState({});
  const [showDecisionDialog, setShowDecisionDialog] = useState(false);
  const [decision, setDecision] = useState('');
  const [justificacion, setJustificacion] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    if (config) {
      calculateScore();
    }
  }, [factoresSeleccionados, config]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await api.get('/ros-scoring/config');
      setConfig(res.data);
      
      // Inicializar checklist
      const initialChecklist = {};
      res.data.checklist_evaluacion?.forEach(item => {
        initialChecklist[item.id] = false;
      });
      setChecklist(initialChecklist);
    } catch (error) {
      toast.error('Error al cargar configuración de scoring');
    } finally {
      setLoading(false);
    }
  };

  const calculateScore = async () => {
    if (!config) return;
    
    try {
      const res = await api.post('/ros-scoring/calculate', {
        caso_id: casoId,
        factores_manuales: factoresSeleccionados
      });
      setScoreResult(res.data);
    } catch (error) {
      console.error('Error calculando score:', error);
    }
  };

  const toggleFactor = (factorId) => {
    setFactoresSeleccionados(prev => ({
      ...prev,
      [factorId]: !prev[factorId]
    }));
  };

  const toggleCategory = (categoria) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoria]: !prev[categoria]
    }));
  };

  const handleDecision = async () => {
    if (decision === 'NO_GENERAR_ROS' && !justificacion.trim()) {
      toast.error('La justificación es obligatoria cuando no se genera ROS');
      return;
    }
    
    // Validar checklist
    const checklistItems = config?.checklist_evaluacion || [];
    const missingRequired = checklistItems
      .filter(item => item.obligatorio && !checklist[item.id])
      .map(item => item.pregunta);
    
    if (missingRequired.length > 0) {
      toast.error('Complete todos los ítems obligatorios del checklist');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/ros-scoring/evaluate', {
        caso_id: casoId,
        score_result: scoreResult,
        checklist,
        decision,
        justificacion
      });
      
      toast.success(decision === 'GENERAR_ROS' 
        ? 'Evaluación guardada. Proceda a generar el ROS.' 
        : 'Evaluación guardada. Caso marcado sin ROS.');
      
      setShowDecisionDialog(false);
      
      if (onEvaluationComplete) {
        onEvaluationComplete(decision);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar evaluación');
    } finally {
      setSaving(false);
    }
  };

  // Agrupar factores por categoría
  const factoresByCategoria = config?.factores?.reduce((acc, factor) => {
    if (!acc[factor.categoria]) {
      acc[factor.categoria] = [];
    }
    acc[factor.categoria].push(factor);
    return acc;
  }, {}) || {};

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <RefreshCw className="animate-spin text-purple-600" size={32} />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Score Result */}
      {scoreResult && (
        <Card 
          className="border-2"
          style={{ borderColor: scoreResult.color }}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div 
                  className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                  style={{ backgroundColor: scoreResult.color }}
                >
                  {scoreResult.score_total}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    Riesgo {scoreResult.nivel_riesgo.charAt(0).toUpperCase() + scoreResult.nivel_riesgo.slice(1)}
                  </h3>
                  <p className="text-slate-500">
                    {scoreResult.score_total} de {scoreResult.score_maximo} puntos ({scoreResult.porcentaje}%)
                  </p>
                  <p className="text-sm mt-1" style={{ color: scoreResult.color }}>
                    {RECOMENDACION_TEXT[scoreResult.recomendacion]}
                  </p>
                </div>
              </div>
              
              <div className="text-right">
                <Badge 
                  className="text-lg px-4 py-1"
                  style={{ backgroundColor: scoreResult.color, color: '#fff' }}
                >
                  {scoreResult.cantidad_factores_aplicados} factores detectados
                </Badge>
              </div>
            </div>
            
            {/* Barra de progreso */}
            <div className="mt-4 h-3 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full transition-all duration-500"
                style={{ 
                  width: `${scoreResult.porcentaje}%`,
                  backgroundColor: scoreResult.color 
                }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Factores de Riesgo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="text-purple-600" />
            Evaluación de Factores de Riesgo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(factoresByCategoria).map(([categoria, factores]) => {
              const isExpanded = expandedCategories[categoria] !== false;
              const selectedCount = factores.filter(f => factoresSeleccionados[f.id]).length;
              const catInfo = config?.categorias?.find(c => c.id === categoria);
              
              return (
                <div key={categoria} className="border rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleCategory(categoria)}
                    className="w-full px-4 py-3 bg-slate-50 flex items-center justify-between hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{catInfo?.nombre || categoria}</span>
                      {selectedCount > 0 && (
                        <Badge className="bg-purple-100 text-purple-700">
                          {selectedCount} seleccionados
                        </Badge>
                      )}
                    </div>
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  
                  {isExpanded && (
                    <div className="p-3 space-y-2">
                      {factores.filter(f => f.activo).map(factor => (
                        <label
                          key={factor.id}
                          className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            factoresSeleccionados[factor.id] 
                              ? 'bg-purple-50 border-2 border-purple-300' 
                              : 'bg-white border border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <Checkbox
                            checked={factoresSeleccionados[factor.id] || false}
                            onCheckedChange={() => toggleFactor(factor.id)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-slate-900">{factor.nombre}</span>
                              <Badge 
                                variant="outline" 
                                className={factoresSeleccionados[factor.id] ? 'bg-purple-100 text-purple-700' : ''}
                              >
                                +{factor.puntos_base} pts
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500 mt-1">{factor.descripcion}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="text-green-600" />
            Checklist de Verificación
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {config?.checklist_evaluacion?.map(item => (
              <label
                key={item.id}
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  checklist[item.id] 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-slate-50 border border-slate-200'
                }`}
              >
                <Checkbox
                  checked={checklist[item.id] || false}
                  onCheckedChange={(checked) => setChecklist(prev => ({ ...prev, [item.id]: checked }))}
                />
                <span className={`flex-1 ${checklist[item.id] ? 'text-green-700' : 'text-slate-700'}`}>
                  {item.pregunta}
                </span>
                {item.obligatorio && (
                  <Badge variant="outline" className="text-red-600 border-red-300">
                    Obligatorio
                  </Badge>
                )}
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Botones de Decisión */}
      <div className="flex gap-4">
        <Button
          className="flex-1 bg-green-600 hover:bg-green-700 py-6"
          onClick={() => {
            setDecision('GENERAR_ROS');
            setJustificacion('');
            setShowDecisionDialog(true);
          }}
        >
          <FileText size={20} className="mr-2" />
          Generar ROS
        </Button>
        <Button
          variant="outline"
          className="flex-1 py-6 border-slate-300"
          onClick={() => {
            setDecision('NO_GENERAR_ROS');
            setJustificacion('');
            setShowDecisionDialog(true);
          }}
        >
          <XCircle size={20} className="mr-2" />
          No Generar ROS
        </Button>
      </div>

      {/* Dialog de Decisión */}
      <Dialog open={showDecisionDialog} onOpenChange={setShowDecisionDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {decision === 'GENERAR_ROS' ? (
                <>
                  <FileText className="text-green-600" />
                  Confirmar Generación de ROS
                </>
              ) : (
                <>
                  <AlertTriangle className="text-amber-600" />
                  Confirmar NO Generar ROS
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {decision === 'GENERAR_ROS'
                ? 'Se procederá a crear el Reporte de Operación Sospechosa.'
                : 'Debe justificar por qué no se genera el ROS. Esta decisión quedará registrada.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Resumen del Score */}
            {scoreResult && (
              <div 
                className="p-4 rounded-lg mb-4"
                style={{ backgroundColor: `${scoreResult.color}15`, borderLeft: `4px solid ${scoreResult.color}` }}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">Score de Riesgo:</span>
                  <span className="font-bold" style={{ color: scoreResult.color }}>
                    {scoreResult.score_total} pts - {scoreResult.nivel_riesgo.toUpperCase()}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  {scoreResult.cantidad_factores_aplicados} factores de riesgo detectados
                </p>
              </div>
            )}
            
            <div>
              <Label>
                {decision === 'GENERAR_ROS' ? 'Comentario (opcional)' : 'Justificación *'}
              </Label>
              <Textarea
                value={justificacion}
                onChange={(e) => setJustificacion(e.target.value)}
                placeholder={
                  decision === 'GENERAR_ROS'
                    ? 'Agregue comentarios adicionales si lo desea...'
                    : 'Explique detalladamente por qué no se genera el ROS...'
                }
                rows={4}
                className="mt-2"
                style={{ backgroundColor: '#fff' }}
              />
              {decision === 'NO_GENERAR_ROS' && (
                <p className="text-xs text-amber-600 mt-2">
                  * Esta justificación quedará registrada en el historial del caso y podrá ser auditada.
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecisionDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleDecision}
              disabled={saving || (decision === 'NO_GENERAR_ROS' && !justificacion.trim())}
              className={decision === 'GENERAR_ROS' ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'}
            >
              {saving ? 'Guardando...' : 'Confirmar Decisión'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
